import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardList, Scale, Sprout, TrendingUp } from 'lucide-react';
import useSeoMeta from '../hooks/useSeoMeta';
import seoPaginas from '../content/seoPaginas.json';
import { useAuth } from '../contexts/AuthContext';
import PublicNav from '../components/common/PublicNav';
import PublicFooter from '../components/common/PublicFooter';
import { Eyebrow, Surcos } from '../components/landing/brand';
import { usePricesSnapshot } from '../hooks/usePricesSnapshot';
import RodeoBuilder from '../components/public/RodeoBuilder';
import CurrencyToggle, { type Currency } from '../components/public/CurrencyToggle';
import { computeInventory, type RodeoRow } from '../libs/inventoryCalc';
import { formatMoney, formatNumber, formatWeight } from '../utils/formatNumber';
import { ctaPublico } from '../helpers/ctaPublico';

// /calculadora-inventario — manual de marca v1.0 (estilos en components/landing/brand.css).
// H1, título y descripción se mantienen (la página está indexada).

const STORAGE_KEY = 'mcd:inv-calc:v1';
const DEFAULT_ROWS: RodeoRow[] = [
  { categoria: 'novillos', cabezas: 10, peso: 380 },
  { categoria: 'vaquillonas', cabezas: 8, peso: 300 },
  { categoria: 'vacas', cabezas: 20, peso: 420 },
];

// Lo que hace la cuenta (verificado en la app: valuación del stock, lector +
// balanza por Bluetooth en el celular o USB en la compu, engorde).
const CON_TU_CUENTA = [
  { Icon: ClipboardList, title: 'Tu stock, sin cargarlo dos veces', desc: 'Toma tus animales por categoría y peso de lo que ya registraste.' },
  { Icon: Scale, title: 'Pesos de la balanza', desc: 'Conectás la balanza y el lector de caravana al celular o a la compu, y los pesos entran solos.' },
  { Icon: TrendingUp, title: 'Valor con cada precio nuevo', desc: 'Cuando se publican los precios de referencia del mes, el valor se recalcula.' },
  { Icon: Sprout, title: 'Margen de engorde', desc: 'Costo por kilo y rentabilidad del engorde, con tus costos reales.' },
];

function loadRows(): RodeoRow[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_ROWS;
}

const CalculadoraInventarioPublicaPage: React.FC = () => {
  const { user } = useAuth();

  useSeoMeta(seoPaginas.calculadoraInventario);

  const { data, loading } = usePricesSnapshot();
  const [rows, setRows] = useState<RodeoRow[]>(loadRows);
  const [currency, setCurrency] = useState<Currency>('ARS');

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
    } catch {
      /* storage no disponible */
    }
  }, [rows]);

  const usdRate = data?.usd.available ? data.usd.rate : null;
  const result = useMemo(
    () => computeInventory(rows, data?.latest ?? {}, usdRate),
    [rows, data, usdRate]
  );

  const enUsd = currency === 'USD' && result.totalUsd != null;
  const totalDisplay = enUsd ? `US$ ${formatNumber(result.totalUsd!, { decimals: 2 })}` : formatMoney(result.totalArs);

  const cats = Object.values(result.byCategory);

  return (
    <div className="lp">
      <PublicNav />

      <main>
        <section className="lp-ph" style={{ paddingBottom: 40 }}>
          <div className="lp-wrap">
            <Eyebrow>Herramienta gratis</Eyebrow>
            <h1>Calculadora de valor de inventario</h1>
            <p className="lp-lead" style={{ maxWidth: 720 }}>
              Armá tu rodeo por categoría y peso, y te decimos cuánto vale hoy usando el promedio mensual de precios en
              remates. En pesos o dólares.
            </p>
            {data?.latestMonth && (
              <span className="lp-stamp">
                <i aria-hidden="true" />
                Actualizado: {data.latestMonth.label}
              </span>
            )}
          </div>
        </section>

        <section className="lp-sec" style={{ paddingTop: 8 }}>
          <div className="lp-wrap lp-tool-grid">
            <div className="lp-card">
              <div className="lp-card__h">
                <h2>Tu rodeo</h2>
              </div>
              <RodeoBuilder rows={rows} onChange={setRows} />
              <p className="lp-note" style={{ marginTop: 18 }}>
                El precio de cada categoría sale del promedio por kilo del último mes disponible. Si una categoría no
                tiene precio, no suma al total.
              </p>
            </div>

            <div className="lp-stack" aria-live="polite">
              {loading ? (
                <div className="lp-skel" style={{ height: 220 }} />
              ) : (
                <>
                  <div className="lp-card lp-card--lift lp-result">
                    <div className="lp-card__h" style={{ marginBottom: 4 }}>
                      <small>Valor total del rodeo</small>
                      {/* Const. no aplica: el valor ya está en pesos de hoy. */}
                      <CurrencyToggle value={currency} onChange={setCurrency} disabled={!usdRate} usdRate={usdRate} uvaAvailable={false} />
                    </div>
                    <b>{totalDisplay}</b>
                    <div className="lp-metrics" style={{ marginTop: 16 }}>
                      <div className="lp-metric">
                        <small>Cabezas</small>
                        <b>{formatNumber(result.totalCabezas)}</b>
                      </div>
                      <div className="lp-metric">
                        <small>Kilos totales</small>
                        <b>{formatWeight(result.totalKg)}</b>
                      </div>
                    </div>
                    {currency === 'USD' && result.totalUsd == null && (
                      <p className="lp-note">Cotización USD no disponible para mostrar en dólares.</p>
                    )}
                  </div>

                  {cats.length > 0 && (
                    <div className="lp-card">
                      <div className="lp-card__h">
                        <h3>Desglose por categoría</h3>
                      </div>
                      <div className="lp-table-wrap">
                        <table className="lp-table">
                          <thead>
                            <tr>
                              <th scope="col">Categoría</th>
                              <th scope="col" className="num">Cabezas</th>
                              <th scope="col" className="num">$/kg</th>
                              <th scope="col" className="num">Valor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cats.map((c) => {
                              const valUsd = currency === 'USD' && usdRate ? c.valor / usdRate : null;
                              const valDisplay = valUsd != null ? `US$ ${formatNumber(valUsd, { decimals: 2 })}` : formatMoney(c.valor);
                              return (
                                <tr key={c.categoria}>
                                  <td style={{ textTransform: 'capitalize' }}>{c.categoria}</td>
                                  <td className="num">{formatNumber(c.cabezas)}</td>
                                  <td className="num">{formatMoney(c.precioKg)}</td>
                                  <td className="num strong">{valDisplay}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        <section className="lp-sec lp-dark">
          <div className="lp-wrap">
            <div className="lp-sec-h" style={{ maxWidth: 760 }}>
              <Eyebrow>Con tu cuenta gratis</Eyebrow>
              <h2 className="lp-h2">Calculalo con tus animales reales.</h2>
              <p className="lp-lead">Esta calculadora te pide cargar a mano. Con tu cuenta, el valor sale de tu propio rodeo.</p>
            </div>
            <div className="lp-perks">
              {CON_TU_CUENTA.map(({ Icon, title, desc }) => (
                <div key={title} className="lp-perk">
                  <Icon className="lp-ico" aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
            <div className="lp-ctas">
              <Link className="lp-btn lp-btn--primary" to={ctaPublico(user).to}>
                {ctaPublico(user).label} <ArrowRight aria-hidden="true" />
              </Link>
              <Link className="lp-btn lp-btn--outline" to="/precios">
                Ver precios de la hacienda
              </Link>
            </div>
          </div>
          <Surcos color="blanco" style={{ right: -120, bottom: -260, width: 860 }} />
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};

export default CalculadoraInventarioPublicaPage;
